import {
    useState, useRef, useEffect, useCallback
} from 'react';
import {
    chatWithAssistant
} from '../configApi';
import {
    Button
} from 'shared/components/ui/button';
import {
    cn
} from 'shared/lib/utils';
import {
    Send, Loader2, MessageSquare, Bot, User, GripVertical, ChevronRight, AlertCircle
} from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Prism as SyntaxHighlighter
} from 'react-syntax-highlighter';
import {
    darcula
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import CopyButton from 'shared/components/CopyButton';
import 'builders/builderInternal/components/aiLab.css';

// --- Markdown renderer config ------------------------------------------------

const markdownComponents = {
    code({
        inline, className, children, ...props
    }) {
        const match = /language-(\w+)/.exec(className || '');
        const code = String(children).replace(/\n$/, '');

        inline = !className && !/\n/.test(code);
        if (inline) {
            return (
                <code className="bg-gray-200 text-gray-800 rounded-[4px] px-1 text-xs font-mono" style={{
                    whiteSpace: 'pre-wrap'
                }}>
                    {children}
                </code>
            );
        }

        if (!inline && match) {
            return (
                <div className="relative rounded-[12px]">
                    <span className="absolute top-2 left-2 bg-gray-600 text-white text-xs px-2 py-1 rounded select-none">
                        {match[1]}
                    </span>
                    <CopyButton code={code} className="text-gray-200" />
                    <SyntaxHighlighter
                        style={darcula}
                        language={match[1] !== 'markdown' ? match[1] : ''}
                        PreTag="div"
                        {...props}
                        customStyle={{
                            paddingTop: '2.5rem'
                        }}
                        codeTagProps={{
                            className: 'whitespace-pre-wrap break-words'
                        }}
                    >
                        {code}
                    </SyntaxHighlighter>
                </div>
            );
        }

        return (
            <div className="bg-[#2c2c2c] rounded-md p-2 pt-[1.5rem] text-xs text-gray-200 font-mono whitespace-pre-wrap relative" style={{
                overflowX: 'auto'
            }}>
                <CopyButton code={code} className="text-gray-200" />
                {children}
            </div>
        );
    },
};

// --- Generic recursive output renderer ---------------------------------------
//
// Rendering decision tree (type-based + recursive for objects):
//
//   Top level:
//     errors[] present           → error banner
//     1 key + long string        → primary markdown (no label)
//     first key is long string   → primary markdown + sections for rest
//     otherwise                  → all keys as labeled sections
//
//   Per value (recursive):
//     null/undefined             → "Not available"
//     string (≤120ch, no \n)     → inline text
//     string (>120ch or \n)      → markdown block
//     number                     → monospace display
//     boolean                    → ✓ True / ✗ False
//     string[] (RAG chunks)      → source cards (detected at any depth)
//     string[] (all <40ch)       → chips
//     string[] (any ≥40ch)       → bulleted list
//     number[]                   → inline badges
//     object[] (homogeneous)     → table
//     object (≤6 keys, all primitive) → compact kv grid
//     object (has non-primitive values) → recursive sub-sections
//     deeply nested (depth ≥ 3)  → collapsible JSON fallback
//     anything else              → collapsible JSON fallback
//

const MAX_RENDER_DEPTH = 3;

function formatLabel(key) {
    return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase());
}

function getTypeTag(value) {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return 'str';
    if (typeof value === 'number') return 'num';
    if (typeof value === 'boolean') return 'bool';
    if (Array.isArray(value)) {
        if (value.length === 0) return 'list';
        if (value.every(v => typeof v === 'string')) return 'str[]';
        if (value.every(v => typeof v === 'number')) return 'num[]';
        if (value.every(v => typeof v === 'object' && v !== null && !Array.isArray(v))) return 'obj[]';
        return 'list';
    }
    if (typeof value === 'object') return 'obj';
    return '?';
}

// RAG chunk detection — stable format from Project O
// rag_dependency._compile_chunks_and_urls: f"Document {i} – {name} {page_str}:\n{text}\n"
const RAG_CHUNK_RE = /^Document \d+ – (.+?)(?:\s*\(Page (\d+)\))?:\n([\s\S]*)$/;

function parseRagChunk(text) {
    const m = RAG_CHUNK_RE.exec(text);
    if (!m) return null;
    return {
        filename: m[1].trim(),
        page: m[2] || null,
        text: m[3].trim()
    };
}

function isRagChunkArray(arr) {
    return arr.length > 0 && arr.every(s => typeof s === 'string' && RAG_CHUNK_RE.test(s));
}

function isFlat(obj) {
    const entries = Object.entries(obj);
    return entries.length > 0 && entries.length <= 6 && entries.every(([_, v]) => v === null || (typeof v !== 'object'));
}

// --- Leaf value renderers ----------------------------------------------------

function RagChunkList({
    items
}) {
    return (
        <div className="flex flex-col gap-1.5">
            {items.map((s, i) => {
                const parsed = parseRagChunk(s);
                if (!parsed) return <div key={i} className="text-sm">{s}</div>;
                return (
                    <div key={i} className="bg-background border border-border/50 rounded-md px-3 py-2.5 border-l-[3px] border-l-primary/30 hover:border-l-primary/60 transition-colors">
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">{i + 1}</span>
                            <span className="text-xs font-semibold text-foreground">{parsed.filename}</span>
                            {parsed.page && <span className="text-[10px] text-muted-foreground">Page {parsed.page}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-4 leading-relaxed">{parsed.text}</p>
                    </div>
                );
            })}
        </div>
    );
}

function StringValue({
    value
}) {
    const isLong = value.length > 120 || value.includes('\n');
    if (isLong) {
        return (
            <div className="text-xs break-words whitespace-pre-wrap">
                <Markdown className="ai-lab-markdown" remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {value}
                </Markdown>
            </div>
        );
    }
    return <span className="text-sm text-foreground">{value}</span>;
}

function NumberValue({
    value
}) {
    return <span className="font-mono text-lg font-bold text-primary">{value}</span>;
}

function BooleanValue({
    value
}) {
    return (
        <span className={cn('inline-flex items-center gap-1.5 text-sm font-semibold', value ? 'text-green-600' : 'text-muted-foreground')}>
            <span className={cn('w-[18px] h-[18px] rounded-full flex items-center justify-center text-[11px]', value ? 'bg-green-100' : 'bg-muted')}>
                {value ? '✓' : '✗'}
            </span>
            {value ? 'True' : 'False'}
        </span>
    );
}

function StringArrayValue({
    items
}) {
    if (isRagChunkArray(items)) return <RagChunkList items={items} />;

    const allShort = items.every(s => s.length < 40);
    if (allShort) {
        return (
            <div className="flex flex-wrap gap-1.5">
                {items.map((s, i) => (
                    <span key={i} className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border/50">{s}</span>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1">
            {items.map((s, i) => (
                <div key={i} className="flex gap-2 text-sm leading-relaxed">
                    <span className="w-[5px] h-[5px] rounded-full bg-primary/40 shrink-0 mt-[7px]" />
                    <span className="break-words whitespace-pre-wrap">{s}</span>
                </div>
            ))}
        </div>
    );
}

function NumberArrayValue({
    items
}) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((n, i) => (
                <span key={i} className="font-mono text-sm font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-md">{n}</span>
            ))}
        </div>
    );
}

function FlatObjectValue({
    value
}) {
    const entries = Object.entries(value);
    return (
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm bg-muted/30 rounded-md px-3 py-2 border border-border/50">
            {entries.map(([k, v]) => (
                <div key={k} className="contents">
                    <span className="text-xs text-muted-foreground font-medium py-0.5 whitespace-nowrap">{formatLabel(k)}</span>
                    <span className={cn('text-xs py-0.5 break-words', typeof v === 'number' && 'font-mono')}>
                        {typeof v === 'boolean' ? <BooleanValue value={v} /> : String(v ?? '\u2014')}
                    </span>
                </div>
            ))}
        </div>
    );
}

function ObjectArrayTable({
    items
}) {
    const keys = [...new Set(items.flatMap(obj => Object.keys(obj)))];
    return (
        <div className="overflow-x-auto border border-border/50 rounded-md">
            <table className="w-full border-collapse text-xs">
                <thead>
                    <tr>
                        {keys.map(k => (
                            <th key={k} className="text-left px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50 border-b border-border whitespace-nowrap">
                                {formatLabel(k)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((row, i) => (
                        <tr key={i} className="hover:bg-muted/30">
                            {keys.map(k => {
                                const v = row[k];
                                const isNum = typeof v === 'number';
                                const isBool = typeof v === 'boolean';
                                return (
                                    <td key={k} className={cn('px-3 py-[7px] border-b border-border/50', isNum && 'font-mono')}>
                                        {isBool ? <BooleanValue value={v} /> : String(v ?? '\u2014')}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CollapsibleJson({
    data
}) {
    const [open, setOpen] = useState(false);
    const json = JSON.stringify(data, null, 2);
    const summary = Array.isArray(data) ? `${data.length} items` : `${Object.keys(data).length} keys`;
    return (
        <div>
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
                <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
                {open ? 'Collapse' : 'Expand'} ({summary})
            </button>
            {open && (
                <div className="relative mt-1.5">
                    <CopyButton code={json} className="text-gray-200" />
                    <pre className="bg-[#1f2937] text-gray-200 rounded-md p-3 pt-8 text-[11.5px] font-mono overflow-x-auto whitespace-pre-wrap break-words leading-relaxed">
                        {json}
                    </pre>
                </div>
            )}
        </div>
    );
}

// --- Recursive object renderer (sub-sections) --------------------------------

function RecursiveObjectValue({
    value, depth
}) {
    const entries = Object.entries(value);
    return (
        <div className={cn(depth > 1 && 'pl-2 border-l-2 border-border/40 mt-1')}>
            {entries.map(([k, v], i) => (
                <div key={k} className={cn('py-1.5', i > 0 && 'border-t border-border/30')}>
                    <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[11px] font-semibold text-muted-foreground capitalize">{formatLabel(k)}</span>
                        <span className="text-[9px] font-medium font-mono px-1 py-px rounded bg-muted text-muted-foreground">{getTypeTag(v)}</span>
                        {Array.isArray(v) && v.length > 0 && (
                            <span className="text-[9px] font-medium text-muted-foreground bg-muted px-1.5 py-px rounded-full">{v.length}</span>
                        )}
                    </div>
                    <ValueRenderer value={v} depth={depth} />
                </div>
            ))}
        </div>
    );
}

// --- Main value dispatcher (type-based, recursive) ---------------------------

function ValueRenderer({
    value, depth = 1
}) {
    if (value === null || value === undefined) {
        return <span className="text-xs text-muted-foreground italic">Not available</span>;
    }
    if (typeof value === 'string') return <StringValue value={value} />;
    if (typeof value === 'number') return <NumberValue value={value} />;
    if (typeof value === 'boolean') return <BooleanValue value={value} />;

    if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-xs text-muted-foreground italic">Empty list</span>;
        if (value.every(v => typeof v === 'string')) return <StringArrayValue items={value} />;
        if (value.every(v => typeof v === 'number')) return <NumberArrayValue items={value} />;
        if (value.every(v => typeof v === 'object' && v !== null && !Array.isArray(v))) {
            return <ObjectArrayTable items={value} />;
        }
        return <CollapsibleJson data={value} />;
    }

    if (typeof value === 'object') {
        // Flat object with all short primitives → compact kv grid
        if (isFlat(value)) return <FlatObjectValue value={value} />;
        // Depth limit → collapsible JSON
        if (depth >= MAX_RENDER_DEPTH) return <CollapsibleJson data={value} />;
        // Recursive: render each key-value as a sub-section
        return <RecursiveObjectValue value={value} depth={depth + 1} />;
    }

    return <span className="text-sm">{String(value)}</span>;
}

// --- Top-level section + OutputRenderer --------------------------------------

function OutputSection({
    label, value
}) {
    const tag = getTypeTag(value);
    const isArr = Array.isArray(value);

    return (
        <div className="bg-card px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{formatLabel(label)}</span>
                <span className="text-[10px] font-medium font-mono px-1.5 py-px rounded bg-muted text-muted-foreground">{tag}</span>
                {isArr && value.length > 0 && (
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-px rounded-full">{value.length}</span>
                )}
            </div>
            <ValueRenderer value={value} depth={1} />
        </div>
    );
}

function OutputRenderer({
    data
}) {
    if (!data) return <span className="text-muted-foreground italic text-xs">No response.</span>;

    const output = data.output;
    const errors = data.errors;

    const hasErrors = errors && Array.isArray(errors) && errors.length > 0;
    const hasOutput = output && typeof output === 'object' && Object.keys(output).length > 0;

    if (!hasOutput && !hasErrors) {
        return <span className="text-muted-foreground italic text-xs">No response.</span>;
    }

    const entries = hasOutput ? Object.entries(output) : [];

    // Single key + long string → primary markdown (no label)
    if (!hasErrors && entries.length === 1 && typeof entries[0][1] === 'string' && entries[0][1].length > 80) {
        return (
            <div className="text-xs break-words whitespace-pre-wrap p-3">
                <Markdown className="ai-lab-markdown" remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {entries[0][1]}
                </Markdown>
            </div>
        );
    }

    // Check if first key is a long string → render as primary + sections for rest
    const firstIsLongStr = entries.length > 0 && typeof entries[0][1] === 'string' && entries[0][1].length > 80;

    return (
        <div className="flex flex-col">
            {hasErrors && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-destructive/10 text-destructive text-xs">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <div>{errors.map((e, i) => <p key={i}>{e}</p>)}</div>
                </div>
            )}

            {firstIsLongStr && (
                <div className="px-3 py-3 text-xs break-words whitespace-pre-wrap">
                    <Markdown className="ai-lab-markdown" remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {entries[0][1]}
                    </Markdown>
                </div>
            )}

            {entries.length > (firstIsLongStr ? 1 : 0) && (
                <div className={cn('flex flex-col gap-px bg-border/30', (hasErrors || firstIsLongStr) && 'border-t border-border/30')}>
                    {entries.slice(firstIsLongStr ? 1 : 0).map(([key, value]) => (
                        <OutputSection key={key} label={key} value={value} />
                    ))}
                </div>
            )}
        </div>
    );
}

// --- Chat component ----------------------------------------------------------

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 448;

// Mirrors DE Studio's AssistantTestPanel: form must surface the UNION of
//   (a) keys declared on `typed_input.{context_keys, propensity_keys, entity_schema.properties}`
//   (b) keys *referenced* by any `assistant.opcodes[i].input_mapping` value
//       matching `^(context|propensity|entity)\.(.+)$`
// Keys present only via mapping are "implicit" — the opcode reads them at
// runtime but the assistant doc didn't formally declare them. They MUST be
// rendered as inputs or chat fails with INPUT_VALIDATION_FAILED on Mind side.
function buildTypedInputDescriptor(assistant, selectedOpcode) {
    const ti = assistant?.typed_input || {};
    const declaredContextKeys = Array.isArray(ti.context_keys) ? ti.context_keys : [];
    const declaredPropensityKeys = Array.isArray(ti.propensity_keys) ? ti.propensity_keys : [];
    const entityProps = ti.entity_schema?.properties || {};
    const declaredEntityKeys = Object.keys(entityProps);

    // Walk every opcode's input_mapping; collect referenced keys per category.
    const refs = {
        context: new Set(),
        propensity: new Set(),
        entity: new Set()
    };
    for (const entry of (assistant?.opcodes || [])) {
        for (const target of Object.values(entry?.input_mapping || {})) {
            const m = (target || '').match(/^(context|propensity|entity)\.(.+)$/);
            if (m) refs[m[1]].add(m[2]);
        }
    }

    const uniq = (arr) => Array.from(new Set(arr));
    const contextKeys = uniq([...declaredContextKeys, ...refs.context]);
    const propensityKeys = uniq([...declaredPropensityKeys, ...refs.propensity]);
    const entityKeys = uniq([...declaredEntityKeys, ...refs.entity]);

    const isImplicit = (cat, k) => {
        if (cat === 'context') return refs.context.has(k) && !declaredContextKeys.includes(k);
        if (cat === 'propensity') return refs.propensity.has(k) && !declaredPropensityKeys.includes(k);
        if (cat === 'entity') return refs.entity.has(k) && !declaredEntityKeys.includes(k);
        return false;
    };

    // Final fallback when nothing on the assistant doc declares anything AND
    // input_mapping is empty: derive context keys from the selected opcode's
    // typed inputs[] (V2 OpCode shape post-A-0008.03).
    const isFullyEmpty = contextKeys.length === 0
        && propensityKeys.length === 0
        && entityKeys.length === 0;
    if (isFullyEmpty) {
        const opcodeInputs = selectedOpcode?.inputs || [];
        const isTypedList = opcodeInputs.length > 0 && typeof opcodeInputs[0] === 'object';
        const fallbackKeys = isTypedList
            ? opcodeInputs.map((i) => i.name).filter(Boolean)
            : opcodeInputs.filter((s) => typeof s === 'string');
        return {
            source: 'opcode',
            contextKeys: fallbackKeys,
            propensityKeys: [],
            entityKeys: [],
            entityProps: {},
            requiredEntityKeys: new Set(),
            intentFormat: ti.intent?.format || 'text',
            entitySchema: null,
            isImplicit: () => false,
        };
    }

    return {
        source: 'assistant',
        contextKeys,
        propensityKeys,
        entityKeys,
        entityProps,
        requiredEntityKeys: new Set(Array.isArray(ti.entity_schema?.required) ? ti.entity_schema.required : []),
        intentFormat: ti.intent?.format || 'text',
        entitySchema: ti.entity_schema || null,
        isImplicit,
    };
}

function safeParseJson(raw, fallback) {
    try { return JSON.parse(raw); } catch { return fallback; }
}

// Tag for keys that the assistant doc didn't formally declare on `typed_input`
// but that an opcode's input_mapping reads from. Mirrors DE Studio's "implicit"
// amber pill so users know the field is real even without a declaration.
function ImplicitBadge() {
    return (
        <span
            title="Used by an opcode's input_mapping but not declared on typed_input"
            className="text-[9px] font-semibold uppercase tracking-wide px-1 py-px rounded"
            style={{
                background: 'rgb(254 243 199)', // amber-100
                color:      'rgb(146 64 14)', // amber-800
            }}
        >
            implicit
        </span>
    );
}

// A-0014 — schema-driven input that branches on the JSON-Schema field shape.
function SchemaDrivenInput({
    schema, value, onChange, disabled
}) {
    if (schema?.enum) {
        return (
            <select
                className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
            >
                <option value="">—</option>
                {schema.enum.map(v => <option key={v} value={v}>{String(v)}</option>)}
            </select>
        );
    }
    if (schema?.type === 'number' || schema?.type === 'integer') {
        return (
            <input
                type="number"
                className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                value={value ?? ''}
                onChange={e => {
                    const v = e.target.value;
                    onChange(v === '' ? undefined : Number(v));
                }}
                disabled={disabled}
            />
        );
    }
    if (schema?.type === 'boolean') {
        return (
            <input
                type="checkbox"
                className="rounded border"
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                disabled={disabled}
            />
        );
    }
    return (
        <input
            type="text"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
        />
    );
}

// A-0014 — Routing trace (rendered when A2A response includes _bex_routing).
function RoutingTrace({
    routing
}) {
    if (!routing || typeof routing !== 'object') return null;
    return (
        <details className="mt-2 ml-2 text-[11px] text-muted-foreground">
            <summary className="cursor-pointer select-none">
                Routing: opcode={routing.chosen_opcode || '—'}
            </summary>
            <div className="mt-1 ml-3 flex flex-col gap-0.5">
                {routing.reason && <div><b>Reason:</b> {routing.reason}</div>}
                {routing.score !== undefined && <div><b>Score:</b> {String(routing.score)}</div>}
                {routing.default_opcode_used && <div><i>Fell back to default opcode</i></div>}
            </div>
        </details>
    );
}

// A-0014 — Structured A2A error parts banner.
function ErrorPartsBanner({
    errorParts
}) {
    if (!Array.isArray(errorParts) || errorParts.length === 0) return null;
    return (
        <div role="alert" className="my-2 rounded border border-destructive/40 bg-destructive/10 p-2 text-[12px] flex flex-col gap-1">
            {errorParts.map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                    <code className="font-mono text-destructive shrink-0">{p.code || p.type || 'ERROR'}</code>
                    <span className="text-destructive/90">{p.message}</span>
                    {p.step_id && (
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-destructive/30 text-destructive/70 shrink-0">
                            step: {p.step_id}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function AssistantChat({
    instanceId, configKey, assistant = null, opcodes = []
}) {
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [sessionId] = useState(() => `chat-${Date.now()}`);
    const [selectedOpcodeIdx, setSelectedOpcodeIdx] = useState(0);
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const bottomRef = useRef(null);
    const resizing = useRef(false);

    const selectedOpcode = opcodes[selectedOpcodeIdx] || null;

    // A-0014 — typed-input descriptor: union of declared typed_input keys + keys
    // referenced by any opcode's input_mapping (mirrors DE Studio AssistantTestPanel).
    const descriptor = buildTypedInputDescriptor(assistant, selectedOpcode);
    const {
        contextKeys, propensityKeys, entityKeys,
        entityProps, requiredEntityKeys,
        intentFormat, isImplicit,
    } = descriptor;

    // Form state — separate buckets per typed-input category.
    const [contextValues, setContextValues] = useState({});
    const [propensityValues, setPropensityValues] = useState({});
    const [entityValues, setEntityValues] = useState({});
    const [intentValue, setIntentValue] = useState('');

    useEffect(() => {
        const c = {}; for (const k of contextKeys) c[k] = '';
        const p = {}; for (const k of propensityKeys) p[k] = '';
        const e = {}; for (const k of entityKeys) e[k] = undefined;
        setContextValues(c);
        setPropensityValues(p);
        setEntityValues(e);
        setIntentValue('');
    }, [selectedOpcodeIdx, opcodes.length, assistant?.assistant_id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [messages]);

    // A-0014 — true when at least one input field has been filled (or, when no
    // typed-input descriptor exists at all, accept the intent textarea alone).
    const hasAnyContext = Object.values(contextValues).some(v => typeof v === 'string' && v.trim());
    const hasAnyPropensity = Object.values(propensityValues).some(v => typeof v === 'string' && v.trim());
    const hasAnyEntity = Object.values(entityValues).some(v => v !== undefined && v !== '');
    const hasIntent = !!(intentValue && intentValue.trim());
    const fullyEmptyDescriptor = contextKeys.length === 0 && propensityKeys.length === 0 && entityKeys.length === 0;
    const canSend = !sending && (hasAnyContext || hasAnyPropensity || hasAnyEntity || hasIntent || fullyEmptyDescriptor);

    const handleSend = async () => {
        if (!canSend) return;

        // Build typed-input payload (A-0007.03 §3 + A-0014).
        const context = {};
        for (const k of contextKeys) {
            if (typeof contextValues[k] === 'string' && contextValues[k].trim()) {
                context[k] = contextValues[k].trim();
            }
        }
        // Free-form fallback when descriptor is empty: parse intent as JSON for context.
        if (fullyEmptyDescriptor && !contextKeys.length) {
            const parsed = safeParseJson(intentValue, null);
            if (parsed && typeof parsed === 'object') {
                Object.assign(context, parsed);
            }
        }

        const propensity = {};
        for (const k of propensityKeys) {
            if (typeof propensityValues[k] === 'string' && propensityValues[k].trim()) {
                propensity[k] = propensityValues[k].trim();
            }
        }

        const entity = {};
        for (const [k, v] of Object.entries(entityValues)) {
            if (v !== undefined && v !== '') entity[k] = v;
        }

        const intent = intentFormat === 'object'
            ? safeParseJson(intentValue, null)
            : (intentValue || null);

        // User-facing summary for the chat bubble.
        const summaryParts = [];
        if (intent && typeof intent === 'string') summaryParts.push(intent);
        for (const [k, v] of Object.entries(context)) summaryParts.push(`${k}: ${v}`);
        for (const [k, v] of Object.entries(entity)) summaryParts.push(`${k}: ${v}`);
        const userContent = summaryParts.join('\n') || '(empty)';

        setMessages(prev => [...prev, {
            role: 'user',
            content: userContent,
            ts: Date.now()
        }]);

        // Reset form.
        const c = {}; for (const k of contextKeys) c[k] = '';
        const p = {}; for (const k of propensityKeys) p[k] = '';
        const e = {}; for (const k of entityKeys) e[k] = undefined;
        setContextValues(c);
        setPropensityValues(p);
        setEntityValues(e);
        setIntentValue('');

        setSending(true);

        try {
            const body = {
                context,
                propensity,
                intent,
                entity,
                session_id: sessionId,
            };
            if (selectedOpcode) {
                body.opcode_id = selectedOpcode._id;
            }

            const data = await chatWithAssistant(instanceId, configKey, body);
            setMessages(prev => [...prev, {
                role: 'assistant',
                data,
                ts: Date.now()
            }]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'error',
                content: err.message,
                ts: Date.now()
            }]);
        } finally {
            setSending(false);
        }
    };

    const onMouseDown = useCallback((e) => {
        e.preventDefault();
        resizing.current = true;
        const startX = e.clientX;
        const startWidth = width;

        const onMouseMove = (e) => {
            if (!resizing.current) return;
            const delta = startX - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
            setWidth(newWidth);
        };

        const onMouseUp = () => {
            resizing.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [width]);

    return (
        <div className="flex flex-col border-l bg-card h-full shrink-0 relative" style={{
            width
        }}>
            <div
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors z-10 flex items-center"
                onMouseDown={onMouseDown}
            >
                <GripVertical className="h-4 w-4 text-muted-foreground/40 -ml-1.5" />
            </div>

            <div className="flex items-center gap-2 px-4 py-3 border-b">
                <MessageSquare className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">Test Chat</span>
            </div>

            {opcodes.length > 1 && (
                <div className="px-4 py-2 border-b">
                    <select
                        className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        value={selectedOpcodeIdx}
                        onChange={e => setSelectedOpcodeIdx(Number(e.target.value))}
                    >
                        {opcodes.map((op, i) => (
                            <option key={op._id} value={i}>{op.name}</option>
                        ))}
                    </select>
                </div>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                        <Bot className="h-10 w-10 opacity-30" />
                        <p className="text-sm text-center">Send a message to test your assistant.</p>
                        {(contextKeys.length > 0 || propensityKeys.length > 0 || entityKeys.length > 0) && (
                            <p className="text-xs text-center">
                                Inputs:{' '}
                                {[...contextKeys, ...propensityKeys, ...entityKeys].join(', ')}
                            </p>
                        )}
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div
                        key={i}
                        className={cn(
                            'flex gap-2',
                            msg.role === 'user' ? 'justify-end' : 'justify-start'
                        )}
                    >
                        {msg.role !== 'user' && (
                            <div className="flex items-start pt-0.5">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Bot className="h-3.5 w-3.5 text-primary" />
                                </div>
                            </div>
                        )}
                        <div
                            className={cn(
                                'max-w-[85%] rounded-lg text-sm overflow-hidden',
                                msg.role === 'user'
                                    ? 'bg-primary text-primary-foreground px-3 py-2'
                                    : msg.role === 'error'
                                        ? 'bg-destructive/10 text-destructive border border-destructive/20 px-3 py-2'
                                        : 'bg-muted border border-border/30'
                            )}
                        >
                            {msg.role === 'assistant' ? (
                                <div>
                                    {/* A-0014 — surface A2A v1.0 structured error parts (when present). */}
                                    <ErrorPartsBanner errorParts={msg.data?.errorParts} />
                                    <OutputRenderer data={msg.data} />
                                    {/* A-0014 — surface A2A v1.0 routing trace (when present). */}
                                    <RoutingTrace routing={msg.data?.routing} />
                                </div>
                            ) : (
                                <pre className="whitespace-pre-wrap font-sans break-words text-[13px] leading-relaxed">{msg.content}</pre>
                            )}
                        </div>
                        {msg.role === 'user' && (
                            <div className="flex items-start pt-0.5">
                                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                                    <User className="h-3.5 w-3.5 text-primary-foreground" />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {sending && (
                    <div className="flex gap-2 justify-start">
                        <div className="flex items-start pt-0.5">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <Bot className="h-3.5 w-3.5 text-primary" />
                            </div>
                        </div>
                        <div className="bg-muted rounded-lg px-3 py-2 text-sm flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Thinking...
                        </div>
                    </div>
                )}

                <div ref={bottomRef} />
            </div>

            <div className="border-t px-3 py-3 space-y-3 max-h-[60%] overflow-y-auto">
                {/* A-0014 — typed-input form. Sections suppressed when empty.
                    When all four are empty AND the descriptor is fully empty (no
                    typed_input + no opcode inputs), the Intent textarea acts as a
                    free-form JSON context fallback. */}

                {contextKeys.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Context</div>
                        {contextKeys.map(k => (
                            <div key={k}>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block flex items-center gap-1.5">
                                    <span>{k.replace(/_/g, ' ')}</span>
                                    {isImplicit('context', k) && <ImplicitBadge />}
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                                    placeholder={k}
                                    value={contextValues[k] || ''}
                                    onChange={e => setContextValues(p => ({
                                        ...p,
                                        [k]: e.target.value
                                    }))}
                                    disabled={sending}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {propensityKeys.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Propensity</div>
                        {propensityKeys.map(k => (
                            <div key={k}>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block flex items-center gap-1.5">
                                    <span>{k.replace(/_/g, ' ')}</span>
                                    {isImplicit('propensity', k) && <ImplicitBadge />}
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                                    placeholder={k}
                                    value={propensityValues[k] || ''}
                                    onChange={e => setPropensityValues(p => ({
                                        ...p,
                                        [k]: e.target.value
                                    }))}
                                    disabled={sending}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {entityKeys.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Entity</div>
                        {entityKeys.map((k) => {
                            const fieldSchema = entityProps[k] || null; // null when implicit
                            const required = requiredEntityKeys.has(k);
                            const hint = fieldSchema?.pattern
                                ? `pattern: ${fieldSchema.pattern}`
                                : fieldSchema?.format
                                    ? `format: ${fieldSchema.format}`
                                    : null;
                            return (
                                <div key={k}>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block flex items-center gap-1.5">
                                        <span>{k.replace(/_/g, ' ')}</span>
                                        {required && <span className="text-destructive">*</span>}
                                        {isImplicit('entity', k) && <ImplicitBadge />}
                                        {hint && <span className="text-[10px] text-muted-foreground/70">({hint})</span>}
                                    </label>
                                    <SchemaDrivenInput
                                        schema={fieldSchema}
                                        value={entityValues[k]}
                                        onChange={v => setEntityValues(p => ({
                                            ...p,
                                            [k]: v
                                        }))}
                                        disabled={sending}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                        {fullyEmptyDescriptor ? 'Free context (JSON or text)' : 'Intent'}
                        {intentFormat === 'object' && <span className="ml-2 text-[10px] text-muted-foreground/70">(JSON)</span>}
                    </label>
                    <textarea
                        className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        rows={2}
                        value={intentValue}
                        onChange={e => setIntentValue(e.target.value)}
                        disabled={sending}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey && (e.metaKey || e.ctrlKey)) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                </div>

                <Button
                    className="w-full gap-2"
                    onClick={handleSend}
                    disabled={!canSend}
                >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                </Button>
            </div>
        </div>
    );
}
